import * as CardCrawl from "@ravalmatic/cardcrawl-engine";
import { GameVars } from "../../../GameVars";
import { BoardContainer } from "../BoardContainer";
import { Deck } from "./Deck";
import { FX } from "./FX/FX";
import { Utils } from "../../../utils/Utils";
import { AudioManager } from "../../../AudioManager";
import { EventType } from "../../../Types";

export class Card extends Phaser.GameObjects.Container {

    public static TIME_SUSTAINED_PRESSING = 400;

    public id: number;
    public cardData: CardCrawl.CardData;
    public valueLabel: Phaser.GameObjects.Text;
    public animatedItems: Phaser.GameObjects.Container;
    public img: Phaser.GameObjects.Image; 
    public fxContainer: Phaser.GameObjects.Container;
    public animatedFrame: Phaser.GameObjects.Sprite;
    public nameLabel: Phaser.GameObjects.Text;
    public dragging: boolean;
    public dropped: boolean;
    public tweening: boolean;

    protected px: number;
    protected py: number;
    
    private dx: number;
    private dy: number;
    private pressTimer: any;
    private pressPointer: {x: number, y: number};
  
    constructor(scene: Phaser.Scene, p: {x: number, y: number, angle: number}, cardData: CardCrawl.CardData) {
        
        super(scene);

        this.cardData = cardData;
        this.id = cardData.id;
        this.px = p.x;
        this.py = p.y;
        this.angle = p.angle;
        this.dragging = false;
        this.tweening = false;
        this.dx = 0;
        this.dy = 0;
        this.dropped = false;
        this.pressTimer = null;

        this.animatedItems = new Phaser.GameObjects.Container(scene);
        this.add(this.animatedItems);

        this.img = new Phaser.GameObjects.Image(scene, 0, 0, "texture_atlas_2", "card_back");
        this.animatedItems.add(this.img);

        if (this.cardData.special || this.cardData.bash || this.cardData.canRevive || this.cardData.reflectDamage) {

            this.animatedFrame = new Phaser.GameObjects.Sprite(scene, 0, 0, "texture_atlas_2", "foil_fx_01");
            this.animatedFrame.visible = false;
            this.animatedFrame.alpha = .6;
            this.animatedFrame.play({key: "foil_fx", startFrame: GameVars.f % 20});
            this.scene.add.existing(this.animatedFrame);
            this.animatedItems.add(this.animatedFrame);
        } 

        if (this.cardData.value !== null) {

            this.valueLabel = new Phaser.GameObjects.Text(scene, 62, -95, this.cardData.lastValue.toString(), {fontFamily: "AlmendraBold", color: "#FFFFFF", fontSize: "35px"});
            
            if (this.cardData.type !== CardCrawl.CardType.PLAYER) {
                this.valueLabel.setStroke("#1E2631", 5);
            } 

            this.valueLabel.setOrigin(.5);
            this.animatedItems.add(this.valueLabel);

        } else {
            this.valueLabel = null;
        }

        if (this.cardData.type !== CardCrawl.CardType.PLAYER) {

            this.nameLabel = new Phaser.GameObjects.Text(scene, 0, 91, Utils.getCardNameStr(this.cardData.type, this.cardData.initialValue), {fontFamily: "AlmendraBold", color: "#FFFFFF", fontSize: "25px"});
            this.nameLabel.setOrigin(.5);
            this.animatedItems.add(this.nameLabel);

            if (this.nameLabel.width > 130) {
                this.nameLabel.setScale(130 / this.nameLabel.width);
            }
            
        } else {
            this.nameLabel = null;
        }

        this.fxContainer = new Phaser.GameObjects.Container(scene);
        this.add(this.fxContainer);

        if (this.cardData.uiData.spawned) {

            if (this.cardData.type === CardCrawl.CardType.PLAYER) {

                this.spawn();

            } else {

                this.visible = false;

                this.scene.time.delayedCall((this.cardData.uiData.spawningIndex + 1) * 200, () => {
                    Deck.currentInstance.dealCard(this.spawn, this);
                }, [], this);
            }
            
        } else {

            this.onCardReady();
        }

        this.scene.sys.updateList.add(this);
    }

    public preUpdate(time: number, delta: number): void {
        
        if (this.dragging && GameVars.allowedMoves !== null) {

            if (!this.scene.input.activePointer.isDown) {
                this.onPointerUp();
                return;
            }

            let x = this.scene.input.activePointer.x - this.parentContainer.x + this.dx;
            let y: number;
 
            if (this.scene.game.device.os.desktop) { 
                y = this.scene.input.activePointer.y - this.parentContainer.y+ this.dy;
            } else {
                y = (this.scene.input.activePointer.y - this.parentContainer.y) / GameVars.scaleY;
            }

            this.x += (x - this.x) * .25;
            this.y += (y - this.y) * .25;

            // esta sobre una de las manos?
            const lowerTableauContainer = BoardContainer.currentInstance.lowerTableauContainer;

            const py = this.y - lowerTableauContainer.y;

            const index = lowerTableauContainer.getOverlappingIndex(x, py);

            if (index === 0 || index === 2) {

                // ESTA SOBRE UNA MANO
                const isMoveValid = Utils.isMoveValid(CardCrawl.TableauType.LOWER, index);

                if (isMoveValid && this.angle === 0) {

                    const angle = lowerTableauContainer.getCardPosition(index).angle;

                    this.scene.tweens.add({
                        targets: this,
                        angle: angle,
                        ease: Phaser.Math.Easing.Cubic.Out,
                        duration: 300
                    });
                }

            } else {

                if (this.angle !== 0) {

                    this.scene.tweens.add({
                        targets: this,
                        angle: 0,
                        ease: Phaser.Math.Easing.Cubic.Out,
                        duration: 300
                    });
                }
            }

            // MIRAR SI ESTA ENCIMA DEL COFRE
            const chest = BoardContainer.currentInstance.chest;

            x = this.x - chest.x;
            y = this.y - chest.y;
    
            if (chest.positionRect.contains(x, y)) {
                chest.onCardOver(this.cardData);
            } else {
                chest.onCardOut();
            }
        }
    }
    
    public moveBackToTableau(): void {

        this.dropped = false;
        this.dragging = false;

        let p: {x: number, y: number, angle: number};

        if (this.cardData.tableau === CardCrawl.TableauType.UPPER) {

            BoardContainer.currentInstance.bringToTop(BoardContainer.currentInstance.upperTableauContainer);
            BoardContainer.currentInstance.upperTableauContainer.add(this);

            p = BoardContainer.currentInstance.upperTableauContainer.getCardPosition(this.cardData.i);
            this.y -= BoardContainer.currentInstance.upperTableauContainer.y;

        } else {

            BoardContainer.currentInstance.bringToTop(BoardContainer.currentInstance.lowerTableauContainer);
            BoardContainer.currentInstance.lowerTableauContainer.add(this);

            p = BoardContainer.currentInstance.lowerTableauContainer.getCardPosition(this.cardData.i);
            this.y -= BoardContainer.currentInstance.lowerTableauContainer.y;
        }

        // PARA QUE NO DE UN SALTO AL AGARRARLO
        this.tweening = true;

        this.scene.tweens.add({
            targets: this,
            x: p.x,
            y: p.y,
            angle: p.angle,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 300,
            onComplete: () => {
                this.tweening = false;
            },
            onCompleteScope: this
        });
    }

    public removeFromScene(cardData: CardCrawl.CardData, inmediately: boolean): void {

        if (inmediately) {

            // VER SI ES LA CARTA QUE SE HA SOLTADO
            if (this.dropped) {

                // ESTO FALLABA EN EL TUTORIAL
                const scene = this.scene || BoardContainer.currentInstance.scene;

                scene.tweens.add({
                    targets: this,
                    alpha: 0,
                    ease: Phaser.Math.Easing.Cubic.Out,
                    duration: 250,
                    onComplete: () => {
                        this.destroy();
                    },
                    onCompleteScope: this
                });

            } else {
                this.destroy();
            }
  
        } else {

            this.cardData = cardData;

            FX.showMessage(this);
            FX.disappear(this);
        }
    }

    protected onCardReady(): void {

        if (this.animatedFrame) {
            this.animatedFrame.visible = true;
        }   
       
        this.x = this.px;
        this.y = this.py;

        // SI LA CARTA SE PUEDE MOVER LE AÑADMMOS LOS LISTNENERS
        const allowedMoves = this.cardData.allowedMoves;

        const canMove = allowedMoves.upperTableau.some(value => value === true) || allowedMoves.lowerTableau.some(value => value === true) || allowedMoves.chest;

        if (canMove) {
            this.img.setInteractive({ useHandCursor: true })
                .on("pointermove", this.onPointerMove, this)
                .on("pointerdown", this.onPointerDown, this)
                .on("pointerup", this.onPointerUp, this)
                .on("pointerout", this.onPointerOut, this)
                .on("pointerover", this.onPointerOver, this);

                const self = this;
                this.scene.game.canvas.addEventListener("mouseout", () => {

                    setTimeout(() => {
                        if (self.scene && self.dragging && !self.dropped) {
                            self.dragging = false;
                            self.dropped = true;
                            BoardContainer.currentInstance.onCardDropped(self);
                        }
                    }, 150);
                    
                });
        }

        FX.addCoinsHeartsEffect(this);

        FX.setImgFrame(this);

        FX.changeValueLabel(this);

        FX.showMessage(this);

        FX.pulse(this);

        FX.goBackToTableau(this);
    }

    protected spawn(): void {

        this.visible = true;

        this.x = 0;
        this.y = -205;

        if (this.animatedFrame) {
            this.animatedFrame.visible = false;
        }

        if (this.valueLabel) {
            this.valueLabel.visible = false;
        }

        if (this.nameLabel) {
            this.nameLabel.visible = false;
        }

        const duration = 300;

        const scene = this.scene || BoardContainer.currentInstance.scene;

        scene.tweens.add({
            targets: this,
            x: this.px,
            y: this.py,  
            duration: duration,
            ease: Phaser.Math.Easing.Cubic.Out,
            onComplete: () => {
                scene.tweens.add({
                    targets: this,
                    scaleX: 0,
                    scaleY: 1.15,   
                    duration: 200,
                    ease: Phaser.Math.Easing.Cubic.Out,
                    onComplete: () => {

                        if (this.animatedFrame) {
                            this.animatedFrame.visible = true;
                        }

                        if (this.valueLabel) {
                            this.valueLabel.visible = true;
                        }

                        if (this.nameLabel) {
                            this.nameLabel.visible = true;
                        }

                        FX.setImgFrame(this);

                        scene.tweens.add({
                            targets: this,
                            scaleX: 1,
                            scaleY: 1,   
                            duration: 200,
                            ease: Phaser.Math.Easing.Cubic.Out,
                            onComplete: () => {
                                if (this.scene) {
                                    this.onCardReady();
                                } 
                            },
                            onCompleteScope: this
                        });
                        
                    },
                    onCompleteScope: this
                });

                AudioManager.playSound("card");
            },
            onCompleteScope: this,
            onStart: () => {

                this.parentContainer?.bringToTop(this);
                GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.CARD_DEALT, []));
            },
            onStartScope: this
        });

        // mirar si ha ido a caer a la mochila
        if (this.cardData.tableau === CardCrawl.TableauType.LOWER && this.cardData.i === 3) {
            FX.audioFXCardDropped(this, CardCrawl.TableauType.LOWER, 3);
        }
    }

    private onPointerMove(): void {

        if (!this.pressPointer) {
            return;
        }

        if (this.pressPointer && this.cardData.special && Math.abs(this.scene.input.activePointer.x - this.pressPointer.x) + Math.abs(this.scene.input.activePointer.y - this.pressPointer.y) > 4) {
            GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.HIDE_MESSAGE, []));
        }
    }

    private onPointerDown(pointer: Phaser.Input.Pointer): void {

        this.pressPointer = {x: pointer.x, y: pointer.y};

        this.pressTimer = setTimeout(() => {

            if (this.pressPointer && this.scene && this.cardData.special && Math.abs(this.scene.input.activePointer.x - this.pressPointer.x) + Math.abs(this.scene.input.activePointer.y - this.pressPointer.y) < 10) {
                GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.SHOW_CARD_DESCRIPTION, [this.cardData]));
            }
            
        }, Card.TIME_SUSTAINED_PRESSING);

        if (GameVars.tutorialMode) {

            const now = Date.now();

            if ( now > GameVars.tutorialStepData.t + GameVars.tutorialStepData.delay) {
                GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.REMOVE_TUTORIAL_CURSOR, []));
            }

            if (GameVars.paused || Utils.isCardSelectionBlocked(this.cardData) || BoardContainer.currentInstance.isCardBeingDragged() || this.tweening || BoardContainer.currentInstance.cardSelectionBlocked) {
                return;
            }

        } else {

            if (GameVars.paused || GameVars.gameOver || BoardContainer.currentInstance.isCardBeingDragged() || this.tweening || BoardContainer.currentInstance.cardSelectionBlocked) {
                return;
            }
        }

        this.dragging = true;
        this.dropped = false;

        const globalPosition = Utils.getGlobalPosition(this);

        this.dx = -this.scene.input.activePointer.x + globalPosition.x;
        this.dy = -this.scene.input.activePointer.y + globalPosition.y;
        
        BoardContainer.currentInstance.add(this);

        // PARA QUE NO DE UN SALTO AL AGARRARLO
        this.x = this.scene.input.activePointer.x - this.parentContainer.x + this.dx;

        if (this.scene.game.device.os.desktop) { 
            this.y = this.scene.input.activePointer.y - this.parentContainer.y + this.dy;
        } else {
            this.y = (this.scene.input.activePointer.y - this.parentContainer.y) / GameVars.scaleY;
        }

        this.setScale(1.035);

        this.scene.tweens.add({
            targets: this,
            angle: 0,
            ease: Phaser.Math.Easing.Cubic.Out,
            duration: 300
        });

        GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.CARD_PICKED, [this]));

        AudioManager.playSound("click");
    }

    private onPointerUp(): void {

        this.pressPointer = null;

        if (this.cardData.special) { // Verifica si el botón derecho del ratón fue presionado
            GameVars.eventsDispatcher.dispatchEvent(new CardCrawl.Event(EventType.HIDE_MESSAGE, []));
        }

        if (this.pressTimer) {
            clearTimeout(this.pressTimer);
            this.pressTimer = null;
        }

        if (GameVars.tutorialMode) {
            if (this.tweening || BoardContainer.currentInstance.cardSelectionBlocked || !this.dragging || GameVars.tutorialStepData.step === 18 || GameVars.tutorialStepData.allowedMoves === null) {
                this.dragging = false;
                this.dropped = true;
                return;
            }
        } else {
            if (GameVars.paused || GameVars.gameOver || this.tweening || BoardContainer.currentInstance.cardSelectionBlocked || !this.dragging) {   
                return;
            }
        }

        this.dragging = false;
        this.dropped = true;

        this.setScale(1);

        BoardContainer.currentInstance.onCardDropped(this);

        AudioManager.playSound("release");
    }

    private onPointerOver(): void {

        if (this.cardData.tableau !== CardCrawl.TableauType.UPPER) {
            return;
        }

        if (GameVars.tutorialMode) {
            if (Utils.isCardSelectionBlocked(this.cardData)) {
                return;
            }
        } else {
            if (GameVars.paused || GameVars.gameOver || BoardContainer.currentInstance.isCardBeingDragged() || this.tweening || BoardContainer.currentInstance.cardSelectionBlocked) {
                return;
            }
        }

        this.setScale(1.05);
        AudioManager.playSound("card_hover");
    }

    private onPointerOut(): void {

        if (this.cardData.tableau !== CardCrawl.TableauType.UPPER) {
            return;
        }

        if (GameVars.tutorialMode) {
            if (Utils.isCardSelectionBlocked(this.cardData)) {
                return;
            }
        } else {
            if (GameVars.paused || GameVars.gameOver || BoardContainer.currentInstance.isCardBeingDragged() || this.tweening || BoardContainer.currentInstance.cardSelectionBlocked) {
                return;
            }
        }

        this.setScale(1);

        AudioManager.playSound("card_hover_out");
    } 
}
